import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ClientPackage,
  ClientPackageWithLedger,
  CreatePackage,
  Package,
  PackageLiabilityReport,
  PackageStatus,
  RedeemPackage,
  SellPackage,
  UpdatePackage,
} from '@salonomy/types';
import { api, unwrap } from '../lib/api.js';
import { qk } from '../lib/query.js';

/**
 * WS-13 Feature 3a — PREPAID PACKAGES / courses (LRU-14). A near-exact CLONE of data/vouchers.ts over the
 * generated client: catalogue CRUD, a per-client purchased-package list, sell / redeem / void, and the
 * salon-wide liability report. TanStack Query, mirroring data/vouchers.ts (paged reads + idempotent
 * money-adjacent mutations) and data/stock.ts (catalogue create/edit + invalidate).
 *
 * HONESTY (non-negotiable, ADR-0009 + ADR-0008): SELLING a package is a LIABILITY, never revenue; revenue
 * (+VAT) is recognised ONLY at REDEMPTION on the real serviced lines. A client_package's REMAINING sessions
 * and value, and the salon's OUTSTANDING liability, are NEVER stored — they are the SIGNED SUM of an
 * append-only ledger the server computes. The UI DISPLAYS the server's derived `remainingValuePence` /
 * `remainingSessions` / `outstandingPence` and POSTs intent; it never recomputes a balance, a remaining
 * session count, or a redeemed total locally. Sell/redeem/void are IDEMPOTENT on a client-supplied
 * `idempotencyKey`: the caller mints it once per intent and gates the button on `isPending`, so a
 * double-tap is a safe replay that moves no total. 422/409 surface faithfully through ApiClientError.
 *
 * Two kinds are DEFERRED server-side and rejected at the SELL route with 422 (exactly as vouchers defers
 * SPV): `purpose='single'` (the SPV deferred-revenue design) and `kind='membership_recurring'` (a separate
 * Stripe-subscription workflow). The catalogue CAN create either (the kind/purpose are carried for
 * forward-compatibility), but the SELL modal must only offer the prepaid kinds — this data layer stays
 * faithful to the typed body and does not itself restrict the choice.
 *
 * Because a SELL takes money (records the sale receipt/payment) and a REDEEM recognises revenue at the till
 * (its redeemed lines land on a receipt), after sell/redeem we invalidate the client-packages list AND the
 * receipts/payments keys so the till + back-office re-read from the server.
 */

export const PACKAGES_PAGE = 50;

export interface PackagePage {
  data: Package[];
  pagination: { limit: number; offset: number; total: number };
}

// ── Reads ──────────────────────────────────────────────────────────────────────────

/**
 * The catalogue packages — paged + filterable by active. The list endpoint returns 200 with an empty page
 * when there is no data, so there is no 404 to map; a genuine error surfaces via `isError`, which the panel
 * renders honestly. `keepPreviousData` keeps the table steady while a filter or page changes.
 */
export function usePackages(active?: boolean, offset = 0) {
  return useQuery({
    queryKey: qk.packages(active ?? null, offset),
    queryFn: async (): Promise<PackagePage> =>
      unwrap(
        await api.GET('/v1/packages', {
          params: { query: { active, limit: PACKAGES_PAGE, offset } },
        }),
      ),
    placeholderData: keepPreviousData,
  });
}

/**
 * One purchased instrument with its full append-only ledger + derived remaining (the staff/till detail
 * view). Gated on an id so a closed modal doesn't fire. Reads live (staleTime 0): the remaining is a
 * money-adjacent server figure the till re-reads.
 */
export function useClientPackage(id: string | null) {
  return useQuery({
    queryKey: id ? qk.clientPackage(id) : ['client-package', 'none'],
    enabled: Boolean(id),
    staleTime: 0,
    queryFn: async (): Promise<ClientPackageWithLedger> =>
      unwrap(await api.GET('/v1/client-packages/{id}', { params: { path: { id: id! } } })),
  });
}

/**
 * A client's purchased packages with the SERVER's DERIVED remaining sessions/value (never stored). Gated
 * on a clientId so a walk-in / closed strip doesn't fire. Reads live (staleTime 0): a remaining balance is
 * the source of truth the till and the client record re-read. Optionally status-filtered.
 */
export function useClientPackages(clientId: string | null, status?: PackageStatus) {
  return useQuery({
    queryKey: qk.clientPackages(clientId ?? null, status ?? null),
    enabled: Boolean(clientId),
    staleTime: 0,
    queryFn: async (): Promise<ClientPackage[]> =>
      unwrap(
        await api.GET('/v1/client-packages', {
          params: { query: { clientId: clientId!, status } },
        }),
      ),
  });
}

/**
 * The package liability reconciliation report (ADR-0009 §reconciliation invariant): sold / redeemed /
 * expired / outstanding, with `reconciles` asserting they net to the penny. Manager-only on the API. Reads
 * live (staleTime 0) — a liability total is a money figure, re-read after any sell/redeem/void.
 */
export function usePackageLiabilityReport(enabled = true) {
  return useQuery({
    queryKey: qk.packageLiability,
    enabled,
    staleTime: 0,
    queryFn: async (): Promise<PackageLiabilityReport> =>
      unwrap(await api.GET('/v1/reports/package-liability')),
  });
}

// ── Catalogue mutations (manager + on the API) ──────────────────────────────────────

/**
 * Create a catalogue package + its items (manager). The body's `kind`/`purpose` carry server defaults the
 * generated type over-requires; cast at the boundary — the server re-validates against the same Zod. A new
 * catalogue SKU never touches a sold instrument's frozen price, so only the catalogue list is invalidated.
 */
export function useCreatePackage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreatePackage): Promise<Package> =>
      unwrap(await api.POST('/v1/packages', { body: body as never })),
    onSuccess: () => invalidatePackages(qc),
  });
}

/**
 * Edit / deactivate a catalogue package (manager). price/purpose/kind are IMMUTABLE on the API once any
 * instrument is sold against it (the server 409/422s) — a sold instrument freezes its own original amount,
 * so a catalogue edit never re-prices a live sale. `active: false` archives it from the sell list. A
 * catalogue edit never moves a liability, so only the catalogue list + the one package are re-based.
 */
export function useUpdatePackage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; body: UpdatePackage }): Promise<Package> =>
      unwrap(
        await api.PATCH('/v1/packages/{id}', {
          params: { path: { id: input.id } },
          body: input.body as never,
        }),
      ),
    onSuccess: (pkg) => {
      qc.setQueryData(qk.package(pkg.id), pkg);
      invalidatePackages(qc);
    },
  });
}

// ── Instrument mutations (all idempotent; gate the button on `isPending`) ────────────

/**
 * SELL a package to a client — issue the instrument + record the SALE as a balanced event (credit
 * package_liability + debit cash/card for the price; a COMP debits comp_expense, no money in). Idempotent on
 * `idempotencyKey`. The API REJECTS `purpose='single'` and `kind='membership_recurring'` with 422 (both
 * deferred) — the SELL modal must only offer the prepaid kinds. A sale records receipt/payment, so we
 * invalidate the client's packages AND the receipts/payments keys.
 */
export function useSellPackage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: SellPackage): Promise<ClientPackage> =>
      unwrap(await api.POST('/v1/client-packages', { body: body as never })),
    onSuccess: (_cp, body) => invalidateClientPackages(qc, body.clientId),
  });
}

/**
 * REDEEM sessions / value against a purchased package (optional appointment) — recognises revenue + VAT on
 * the real serviced `redeemedLines` and decrements the liability. Idempotent on `idempotencyKey`: a
 * re-delivered redeem returns the original result and moves no total. Returns the instrument with its
 * updated ledger + DERIVED remaining (server figures) so the till shows the new remaining. A redemption
 * lands on a receipt, so invalidate the client's packages + the receipts/payments keys.
 */
export function useRedeemPackage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      body: RedeemPackage;
    }): Promise<ClientPackageWithLedger> =>
      unwrap(
        await api.POST('/v1/client-packages/{id}/redeem', {
          params: { path: { id: input.id } },
          body: input.body as never,
        }),
      ),
    onSuccess: (cp) => {
      qc.setQueryData(qk.clientPackage(cp.id), cp);
      invalidateClientPackages(qc, cp.clientId);
    },
  });
}

/**
 * VOID a purchased package — clears its remaining liability to comp_expense, never silently (reason
 * required). Idempotent on `idempotencyKey` minted once per intent. Returns the instrument with its updated
 * ledger so the caller shows the cleared remaining.
 */
export function useVoidPackage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      reason: string;
      idempotencyKey: string;
    }): Promise<ClientPackageWithLedger> =>
      unwrap(
        await api.POST('/v1/client-packages/{id}/void', {
          params: { path: { id: input.id } },
          body: { reason: input.reason, idempotencyKey: input.idempotencyKey },
        }),
      ),
    onSuccess: (cp) => {
      qc.setQueryData(qk.clientPackage(cp.id), cp);
      invalidateClientPackages(qc, cp.clientId);
    },
  });
}

// ── Invalidation helpers ──────────────────────────────────────────────────────────

/** A catalogue create/edit/archive shifts the catalogue list (sold instruments keep their frozen price). */
function invalidatePackages(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: ['packages'] });
}

/**
 * Any sell/redeem/void shifts the client's purchased-package list, the salon-wide liability totals, AND the
 * till/back-office money the sale or redemption landed on (a sale records a receipt/payment; a redemption
 * recognises revenue on a receipt). Re-read them all together — every derived figure re-reads from the
 * server.
 */
function invalidateClientPackages(qc: ReturnType<typeof useQueryClient>, _clientId: string): void {
  void qc.invalidateQueries({ queryKey: ['client-packages'] });
  void qc.invalidateQueries({ queryKey: qk.packageLiability });
  void qc.invalidateQueries({ queryKey: ['receipts'] });
  void qc.invalidateQueries({ queryKey: ['payments'] });
}
